import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Package, Briefcase, Plus, Edit2, Trash2, Link, Unlink, Cpu, AlertTriangle, Loader2 } from 'lucide-react'
import { containerService } from '../services/containerService'
import { assetService } from '../services/assetService'
import { baliseService } from '../services/baliseService'
import VehicleCreationWizard from './VehicleCreationWizard'
import type { Customer } from '../types/customer'
import type { Container, ContainerRequest } from '../types/container'
import type { Asset, AssetRequest } from '../types/asset'

interface AssetContainerModalProps {
  isOpen: boolean
  onClose: () => void
  customer: Customer | null
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

type SubModal = 'none' | 'containerForm' | 'assetForm' | 'baliseList' | 'deleteProtection'

const AssetContainerModal: React.FC<AssetContainerModalProps> = ({ isOpen, onClose, customer, showMessage }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'containers' | 'assets'>('containers')
  const [containers, setContainers] = useState<Container[]>([])
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)
  const [subModal, setSubModal] = useState<SubModal>('none')
  const [showCreationWizard, setShowCreationWizard] = useState(false)
  const [wizardSource, setWizardSource] = useState<'ASSET' | 'CONTAINER'>('ASSET')

  // Container form (edit only — create uses VehicleCreationWizard)
  const [isEditingContainer, setIsEditingContainer] = useState(false)
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null)
  const [containerForm, setContainerForm] = useState<ContainerRequest>({
    equipmentReference: '', containerType: '20GP', status: 'AVAILABLE', booking_reference: '', owner: '', destination: '', origin: ''
  })

  // Asset form (edit only)
  const [isEditingAsset, setIsEditingAsset] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [assetForm, setAssetForm] = useState<AssetRequest>({
    name: '', type: 'VEHICLE', identifier: '', status: 'ACTIVE', description: '', location: ''
  })

  // Balise list
  const [containerForBalises, setContainerForBalises] = useState<Container | null>(null)
  const [containerBalises, setContainerBalises] = useState<any[]>([])
  const [loadingBalises, setLoadingBalises] = useState(false)

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'container' | 'asset'; id: number; name: string } | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const msg = useCallback((text: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    if (showMessage) showMessage(text, type)
  }, [showMessage])

  const loadData = useCallback(async () => {
    if (!customer?.id) return
    setLoading(true)
    try {
      const [cRes, aRes] = await Promise.all([
        containerService.getCustomerContainers(customer.id),
        assetService.getCustomerAssets(customer.id)
      ])
      setContainers(cRes.data || [])
      setAssets(aRes.data || [])
    } catch (e: any) {
      msg(e.message || 'Failed to load data', 'error')
    } finally {
      setLoading(false)
    }
  }, [customer?.id, msg])

  useEffect(() => {
    if (isOpen && customer?.id) loadData()
  }, [isOpen, customer?.id, loadData])

  if (!isOpen || !customer) return null

  const resetContainerForm = () => {
    setContainerForm({ equipmentReference: '', containerType: '20GP', status: 'AVAILABLE', booking_reference: '', owner: '', destination: '', origin: '' })
    setIsEditingContainer(false)
    setSelectedContainer(null)
  }

  const resetAssetForm = () => {
    setAssetForm({ name: '', type: 'VEHICLE', identifier: '', status: 'ACTIVE', description: '', location: '' })
    setIsEditingAsset(false)
    setSelectedAsset(null)
  }

  const openCreateContainer = () => {
    setWizardSource('CONTAINER')
    setShowCreationWizard(true)
  }
  const openCreateAsset = () => {
    setWizardSource('ASSET')
    setShowCreationWizard(true)
  }

  const openEditContainer = (c: Container) => {
    const validTypes = ['20GP', '40GP', '40HC', '20RF', '40RF']
    setSelectedContainer(c)
    setIsEditingContainer(true)
    setContainerForm({
      equipmentReference: c.name || '',
      containerType: validTypes.includes(c.container_type) ? c.container_type : '20GP',
      status: c.status || 'AVAILABLE',
      booking_reference: c.booking_reference || '',
      owner: c.owner || '',
      destination: c.destination || '',
      origin: c.origin || '',
    })
    setSubModal('containerForm')
  }

  const openEditAsset = (a: Asset) => {
    setSelectedAsset(a)
    setIsEditingAsset(true)
    setAssetForm({
      name: a.name, type: a.type, identifier: a.identifier,
      status: a.status, description: a.description || '', location: a.location || '',
    })
    setSubModal('assetForm')
  }

  const submitContainer = async () => {
    if (!isEditingContainer || !selectedContainer) return
    if (!containerForm.equipmentReference.trim()) { msg(t('acm.referenceRequired', { defaultValue: 'Equipment reference is required' }), 'warning'); return }
    if (!containerForm.owner?.trim() || !containerForm.destination?.trim()) {
      msg(t('acm.ownerDestRequired', { defaultValue: 'Owner and destination are required' }), 'warning')
      return
    }
    try {
      const payload = { ...containerForm, customerId: customer.id, name: containerForm.equipmentReference }
      await containerService.updateContainer(selectedContainer.id, payload)
      msg(t('acm.containerUpdated', { defaultValue: 'Container updated' }), 'success')
      setSubModal('none')
      resetContainerForm()
      loadData()
    } catch (e: any) { msg(e.message || 'Error', 'error') }
  }

  const submitAsset = async () => {
    if (!isEditingAsset || !selectedAsset) return
    if (!assetForm.name.trim() || !assetForm.identifier.trim()) { msg(t('acm.assetFieldsRequired', { defaultValue: 'Name and identifier required' }), 'warning'); return }
    try {
      const payload = { ...assetForm, customer_id: customer.id }
      await assetService.updateAsset(selectedAsset.id, payload)
      msg(t('acm.assetUpdated', { defaultValue: 'Asset updated' }), 'success')
      setSubModal('none')
      resetAssetForm()
      loadData()
    } catch (e: any) { msg(e.message || 'Error', 'error') }
  }

  const handleDeleteRequest = async (type: 'container' | 'asset', item: Container | Asset) => {
    if (type === 'container') {
      const c = item as Container
      if (c.balise_count && c.balise_count > 0) {
        setContainerForBalises(c)
        setLoadingBalises(true)
        setSubModal('deleteProtection')
        try {
          const res = await containerService.getContainerBalises(c.id)
          setContainerBalises(res.success ? res.data : [])
        } catch { setContainerBalises([]) }
        finally { setLoadingBalises(false) }
        return
      }
    }
    setDeleteTarget({ type, id: item.id, name: (item as any).name || (item as any).identifier })
    setDeleteConfirmText('')
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'container') await containerService.deleteContainer(deleteTarget.id)
      else await assetService.deleteAsset(deleteTarget.id)
      msg(`${deleteTarget.type === 'container' ? 'Container' : 'Asset'} deleted`, 'success')
      setDeleteTarget(null)
      loadData()
    } catch (e: any) { msg(e.message || 'Delete failed', 'error') }
  }

  const openBaliseList = async (c: Container) => {
    setContainerForBalises(c)
    setLoadingBalises(true)
    setSubModal('baliseList')
    try {
      const res = await containerService.getContainerBalises(c.id)
      setContainerBalises(res.success ? res.data : [])
    } catch { setContainerBalises([]) }
    finally { setLoadingBalises(false) }
  }

  const unassignBalise = async (universalId: string) => {
    try {
      await baliseService.unassignBalise(universalId)
      setContainerBalises(prev => prev.filter(b => b.universal_id !== universalId))
      msg(t('acm.baliseUnassigned', { defaultValue: 'Balise unassigned' }), 'success')
      loadData()
    } catch (e: any) { msg(e.message || 'Unassign failed', 'error') }
  }

  const statusColor = (s: string | undefined) => {
    if (!s) return 'bg-gray-100 text-gray-700'
    const u = s.toUpperCase()
    if (['ACTIVE', 'AVAILABLE'].includes(u)) return 'bg-green-100 text-green-800'
    if (['MAINTENANCE', 'IN_TRANSIT'].includes(u)) return 'bg-yellow-100 text-yellow-800'
    if (['INACTIVE', 'OUT_OF_SERVICE'].includes(u)) return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-700'
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{customer.company_name}</h2>
            <p className="text-sm text-gray-500">{t('acm.assetsAndContainers', { defaultValue: 'Assets & Containers' })}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 transition"><X size={20} /></button>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('containers')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'containers' ? 'border-blue-600 text-blue-600 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Package size={16} /> {t('acm.containers', { defaultValue: 'Containers' })} <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200">{containers.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('assets')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'assets' ? 'border-purple-600 text-purple-600 bg-purple-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Briefcase size={16} /> {t('acm.assets', { defaultValue: 'Assets' })} <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200">{assets.length}</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : activeTab === 'containers' ? (
            /* Containers Tab */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">{t('acm.containers', { defaultValue: 'Containers' })}</h3>
                <button onClick={openCreateContainer} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition">
                  <Plus size={14} /> {t('acm.add', { defaultValue: 'Add' })}
                </button>
              </div>
              {containers.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package size={40} className="mx-auto mb-2 opacity-50" />
                  <p>{t('acm.noContainers', { defaultValue: 'No containers yet' })}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {containers.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border hover:shadow-sm transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{c.name}</span>
                          {c.container_type && c.container_type !== c.status && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{c.container_type}</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${statusColor(c.status)}`}>{c.status}</span>
                        </div>
                        {c.booking_reference && <p className="text-xs text-gray-500 mt-0.5">Booking: {c.booking_reference}</p>}
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={() => c.balise_count && c.balise_count > 0 ? openBaliseList(c) : undefined}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${c.balise_count && c.balise_count > 0 ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {c.balise_count && c.balise_count > 0 ? <Link size={12} /> : <Unlink size={12} />}
                          {c.balise_count || 0} {t('acm.balises', { defaultValue: 'balise(s)' })}
                        </button>
                        <button onClick={() => openEditContainer(c)} className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-200"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteRequest('container', c)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Assets Tab */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">{t('acm.assets', { defaultValue: 'Assets' })}</h3>
                <button onClick={openCreateAsset} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition">
                  <Plus size={14} /> {t('acm.add', { defaultValue: 'Add' })}
                </button>
              </div>
              {assets.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Briefcase size={40} className="mx-auto mb-2 opacity-50" />
                  <p>{t('acm.noAssets', { defaultValue: 'No assets yet' })}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assets.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border hover:shadow-sm transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{a.name}</span>
                          <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">{a.type}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${statusColor(a.status)}`}>{a.status}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">ID: {a.identifier}{a.location ? ` | ${a.location}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${a.balise_assigned ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {a.balise_assigned ? <Link size={12} /> : <Unlink size={12} />}
                          {a.balise_assigned ? t('acm.linked', { defaultValue: 'Linked' }) : t('acm.unlinked', { defaultValue: 'Unlinked' })}
                        </span>
                        <button onClick={() => openEditAsset(a)} className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-200"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteRequest('asset', a)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCreationWizard && (
        <VehicleCreationWizard
          isOpen={showCreationWizard}
          initialCustomerId={customer.id}
          initialSource={wizardSource}
          lockCustomer
          onClose={() => setShowCreationWizard(false)}
          showMessage={showMessage}
          onSuccess={() => {
            setShowCreationWizard(false)
            loadData()
          }}
        />
      )}

      {/* Edit Container / Asset forms (create uses VehicleCreationWizard) */}
      {subModal === 'containerForm' && isEditingContainer && (
        <div className="fixed inset-0 z-[310] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <h3 className="font-bold text-gray-900">{t('acm.editContainer', { defaultValue: 'Edit Container' })}</h3>
              <button onClick={() => { setSubModal('none'); resetContainerForm() }} className="p-1.5 rounded-lg hover:bg-gray-200"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.reference', { defaultValue: 'Equipment Reference' })} *</label>
                <input value={containerForm.equipmentReference} onChange={e => setContainerForm(p => ({ ...p, equipmentReference: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="e.g. MSCU1234567" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.type', { defaultValue: 'Container Type' })}</label>
                <select value={containerForm.containerType} onChange={e => setContainerForm(p => ({ ...p, containerType: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="20GP">20GP - 20ft Standard</option>
                  <option value="40GP">40GP - 40ft Standard</option>
                  <option value="40HC">40HC - 40ft High Cube</option>
                  <option value="20RF">20RF - 20ft Reefer</option>
                  <option value="40RF">40RF - 40ft Reefer</option>
                </select>
              </div>
              {isEditingContainer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.status', { defaultValue: 'Status' })}</label>
                  <select value={containerForm.status} onChange={e => setContainerForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="AVAILABLE">Available</option>
                    <option value="ASSIGNED">Assigned</option>
                    <option value="IN_TRANSIT">In Transit</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.owner', { defaultValue: 'Owner' })} *</label>
                <input value={containerForm.owner || ''} onChange={e => setContainerForm(p => ({ ...p, owner: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Owner company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.origin', { defaultValue: 'Origin' })}</label>
                  <input value={containerForm.origin || ''} onChange={e => setContainerForm(p => ({ ...p, origin: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.destination', { defaultValue: 'Destination' })} *</label>
                  <input value={containerForm.destination || ''} onChange={e => setContainerForm(p => ({ ...p, destination: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.bookingRef', { defaultValue: 'Booking Reference' })}</label>
                <input value={containerForm.booking_reference || ''} onChange={e => setContainerForm(p => ({ ...p, booking_reference: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t bg-gray-50">
              <button onClick={() => { setSubModal('none'); resetContainerForm() }} className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-100">
                {t('cancel', { defaultValue: 'Cancel' })}
              </button>
              <button onClick={submitContainer} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" disabled={!containerForm.equipmentReference.trim()}>
                {t('acm.update', { defaultValue: 'Update' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {subModal === 'assetForm' && isEditingAsset && (
        <div className="fixed inset-0 z-[310] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <h3 className="font-bold text-gray-900">{t('acm.editAsset', { defaultValue: 'Edit Asset' })}</h3>
              <button onClick={() => { setSubModal('none'); resetAssetForm() }} className="p-1.5 rounded-lg hover:bg-gray-200"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand / manufacturer *</label>
                <input value={assetForm.name} onChange={e => setAssetForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.assetType', { defaultValue: 'Type' })}</label>
                <select value={assetForm.type} onChange={e => setAssetForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                  <option value="VEHICLE">Unclassified legacy vehicle</option>
                  <option value="TRACTOR_HEAD">Tractor head</option>
                  <option value="TRUCK">Rigid truck</option>
                  <option value="VAN">Van</option>
                  <option value="PICKUP">Pickup</option>
                  <option value="CAR">Car</option>
                  <option value="TANKER">Fuel tanker / cistern trailer</option>
                  <option value="CONTAINER_CHASSIS">Container chassis</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permanent asset code *</label>
                <input value={assetForm.identifier} onChange={e => setAssetForm(p => ({ ...p, identifier: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" placeholder="e.g. VIN, serial number" />
              </div>
              {isEditingAsset && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('acm.status', { defaultValue: 'Status' })}</label>
                  <select value={assetForm.status} onChange={e => setAssetForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                    <option value="ACTIVE">Active</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="DEPLOYED">Deployed</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License plate / registration</label>
                <input value={assetForm.location || ''} onChange={e => setAssetForm(p => ({ ...p, location: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input value={assetForm.description || ''} onChange={e => setAssetForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t bg-gray-50">
              <button onClick={() => { setSubModal('none'); resetAssetForm() }} className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-100">
                {t('cancel', { defaultValue: 'Cancel' })}
              </button>
              <button onClick={submitAsset} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                disabled={!assetForm.name.trim() || !assetForm.identifier.trim()}>
                {t('acm.update', { defaultValue: 'Update' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balise List Sub-Modal */}
      {subModal === 'baliseList' && containerForBalises && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[70vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <div>
                <h3 className="font-bold text-gray-900">{t('acm.balisesFor', { defaultValue: 'Balises for' })} {containerForBalises.name}</h3>
                <p className="text-xs text-gray-500">{containerForBalises.container_type} &middot; {containerForBalises.status}</p>
              </div>
              <button onClick={() => setSubModal('none')} className="p-1.5 rounded-lg hover:bg-gray-200"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {loadingBalises ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
              ) : containerBalises.length === 0 ? (
                <p className="text-center text-gray-400 py-8">{t('acm.noBalises', { defaultValue: 'No balises found' })}</p>
              ) : (
                <div className="space-y-2">
                  {containerBalises.map((b, i) => (
                    <div key={b.lock_code || b.id || i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border">
                      <div className="flex items-center gap-3">
                        <Cpu size={16} className="text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{b.universal_id}</p>
                          <p className="text-xs text-gray-500">{b.brand} &middot; {b.status}</p>
                          {b.battery_level && <p className="text-xs text-gray-400">Battery: {b.battery_level}%</p>}
                        </div>
                      </div>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">{t('acm.assigned', { defaultValue: 'Assigned' })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Protection Sub-Modal (container has balises) */}
      {subModal === 'deleteProtection' && containerForBalises && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[70vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-yellow-50">
              <h3 className="font-bold text-yellow-800 flex items-center gap-2"><AlertTriangle size={18} /> {t('acm.cannotDelete', { defaultValue: 'Cannot Delete' })}</h3>
              <button onClick={() => setSubModal('none')} className="p-1.5 rounded-lg hover:bg-yellow-100"><X size={18} /></button>
            </div>
            <div className="p-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <p className="text-sm text-yellow-800 font-medium">{containerForBalises.name} has {containerForBalises.balise_count} assigned balise(s).</p>
                <p className="text-xs text-yellow-700 mt-1">{t('acm.unassignFirst', { defaultValue: 'Unassign all balises before deleting this container.' })}</p>
              </div>
              {loadingBalises ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-yellow-600" /></div>
              ) : containerBalises.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-green-600 font-medium">{t('acm.allUnassigned', { defaultValue: 'All balises unassigned!' })}</p>
                  <button onClick={() => {
                    setSubModal('none')
                    setDeleteTarget({ type: 'container', id: containerForBalises.id, name: containerForBalises.name })
                    setDeleteConfirmText('')
                  }} className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                    {t('acm.proceedDelete', { defaultValue: 'Proceed with Delete' })}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {containerBalises.map((b, i) => (
                    <div key={b.lock_code || b.id || i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border">
                      <div className="flex items-center gap-3">
                        <Cpu size={16} className="text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{b.universal_id}</p>
                          <p className="text-xs text-gray-500">{b.brand} &middot; {b.status}</p>
                        </div>
                      </div>
                      <button onClick={() => unassignBalise(b.universal_id)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition">
                        <Unlink size={12} /> {t('acm.unassign', { defaultValue: 'Unassign' })}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-sm p-6">
            <h3 className="font-bold text-gray-900 mb-2">{t('acm.confirmDeleteTitle', { defaultValue: 'Confirm Delete' })}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('acm.confirmDeleteMsg', { defaultValue: `Delete ${deleteTarget.type} "${deleteTarget.name}"? This cannot be undone.`, type: deleteTarget.type, name: deleteTarget.name })}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-100">
                {t('cancel', { defaultValue: 'Cancel' })}
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
                {t('delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AssetContainerModal
